import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Calendar,
  RefreshCw,
  Loader2,
  ShoppingBag,
  FileText,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock,
  Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Days in Romanian
const DAYS_RO = ['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'];

export default function ContentCalendarPage() {
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [trendingKeywords, setTrendingKeywords] = useState([]);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  
  const { getAuthHeaders } = useAuth();
  const { currentSite, sites } = useSite();

  useEffect(() => {
    fetchCalendar();
  }, []);

  useEffect(() => {
    if (currentSite?.id) {
      fetchTrendingKeywords();
      fetchWeeklyPlan();
    }
  }, [currentSite?.id]);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/automation/calendar?days=14`, {
        headers: getAuthHeaders()
      });
      setCalendar(response.data.calendar || []);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
      toast.error('Eroare la încărcarea calendarului');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendingKeywords = async () => {
    if (!currentSite?.id) return;
    
    setLoadingKeywords(true);
    try {
      const response = await axios.get(`${API}/automation/trending-keywords/${currentSite.id}`, {
        headers: getAuthHeaders()
      });
      setTrendingKeywords(response.data.keywords || []);
    } catch (error) {
      console.error('Failed to fetch trending keywords:', error);
    } finally {
      setLoadingKeywords(false);
    }
  };

  const fetchWeeklyPlan = async () => {
    if (!currentSite?.id) return;
    
    setLoadingPlan(true);
    try {
      const response = await axios.get(`${API}/automation/weekly-plan/${currentSite.id}`, {
        headers: getAuthHeaders()
      });
      setWeeklyPlan(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly plan:', error);
    } finally {
      setLoadingPlan(false);
    }
  };

  const getSiteInfo = (siteId) => {
    return sites.find(s => s.id === siteId) || {};
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary" />
            Calendar Editorial
          </h1>
          <p className="text-muted-foreground mt-1">
            Planificarea conținutului pentru următoarele 14 zile
          </p>
        </div>
        <Button onClick={fetchCalendar} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizează
        </Button>
      </div>

      {/* Trending Keywords Section */}
      {currentSite && (
        <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Keywords Trending pentru {currentSite.site_name || currentSite.site_url}
            </CardTitle>
            <CardDescription>
              Keywords generate automat cu AI pe baza nișei și sezonului curent
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingKeywords ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Se încarcă keywords...
              </div>
            ) : trendingKeywords.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {trendingKeywords.map((keyword, index) => (
                  <Badge key={index} variant="secondary" className="text-sm px-3 py-1">
                    {keyword}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">Nu s-au găsit keywords</p>
            )}
            <Button 
              onClick={fetchTrendingKeywords} 
              variant="ghost" 
              size="sm" 
              className="mt-3"
              disabled={loadingKeywords}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Regenerează Keywords
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Weekly Plan for Current Site */}
      {currentSite && weeklyPlan && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Plan Săptămânal - {weeklyPlan.site_name}
              {weeklyPlan.is_store && (
                <Badge variant="outline" className="ml-2 bg-orange-500/10 text-orange-500 border-orange-500/20">
                  <ShoppingBag className="w-3 h-3 mr-1" />
                  Magazin
                </Badge>
              )}
            </CardTitle>
            {weeklyPlan.is_store && (
              <CardDescription>
                Articole cu produse: {weeklyPlan.product_days?.join(', ')}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {loadingPlan ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Se generează planul...
              </div>
            ) : weeklyPlan.weekly_plan ? (
              <div className="grid grid-cols-7 gap-2">
                {weeklyPlan.weekly_plan.map((day, index) => (
                  <div 
                    key={index}
                    className={`p-3 rounded-lg border ${
                      day.include_products 
                        ? 'bg-orange-500/10 border-orange-500/30' 
                        : 'bg-secondary/50 border-border'
                    }`}
                  >
                    <div className="font-medium text-sm mb-1 flex items-center justify-between">
                      {day.day}
                      {day.include_products && (
                        <ShoppingBag className="w-3 h-3 text-orange-500" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2" title={day.topic}>
                      {day.topic}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {day.keywords?.slice(0, 2).map((kw, i) => (
                        <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">
                          {kw}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">Nu s-a putut genera planul</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Calendar Grid */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Calendar 14 Zile - Toate Site-urile</CardTitle>
          <CardDescription>
            <span className="inline-flex items-center gap-1 mr-4">
              <div className="w-3 h-3 rounded bg-orange-500/30"></div>
              Zile cu produse (Mi, Sb, Dm)
            </span>
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-green-500" />
              Articol deja planificat
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2 border-b border-border font-medium text-sm text-muted-foreground">
                    Site
                  </th>
                  {calendar.slice(0, 14).map((day, index) => (
                    <th 
                      key={index} 
                      className={`p-2 border-b border-border text-center min-w-[80px] ${
                        day.is_product_day ? 'bg-orange-500/10' : ''
                      }`}
                    >
                      <div className="font-medium text-sm">{day.day_name}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(day.date)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => (
                  <tr key={site.id} className="hover:bg-secondary/50">
                    <td className="p-2 border-b border-border">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate max-w-[150px]" title={site.site_name || site.site_url}>
                          {site.site_name || site.site_url?.replace('https://', '')}
                        </span>
                        {calendar[0]?.sites?.find(s => s.site_id === site.id)?.is_store && (
                          <ShoppingBag className="w-3 h-3 text-orange-500 flex-shrink-0" />
                        )}
                      </div>
                    </td>
                    {calendar.slice(0, 14).map((day, dayIndex) => {
                      const siteDay = day.sites?.find(s => s.site_id === site.id);
                      const hasArticle = siteDay?.existing_article;
                      const includeProducts = siteDay?.include_products;
                      
                      return (
                        <td 
                          key={dayIndex}
                          className={`p-2 border-b border-border text-center ${
                            includeProducts ? 'bg-orange-500/10' : ''
                          }`}
                        >
                          {hasArticle ? (
                            <div className="flex flex-col items-center">
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                              <span className="text-[10px] text-muted-foreground truncate max-w-[70px]" title={hasArticle.title}>
                                {hasArticle.title?.substring(0, 15)}...
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <Clock className="w-4 h-4 text-muted-foreground/50" />
                              {includeProducts && (
                                <span className="text-[10px] text-orange-500">+ produse</span>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="bg-secondary/30">
        <CardContent className="py-4">
          <h3 className="font-medium mb-2">Legendă:</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-500/30"></div>
              <span>Zile cu linkuri produse (Magazine)</span>
            </div>
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-orange-500" />
              <span>Site magazin</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>Articol planificat/publicat</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>În așteptare</span>
            </div>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            <strong>Magazine cu produse:</strong> bestbuybabys.com, azurelady.com, storeforladies.com
            <br />
            <strong>Zile cu linkuri produse:</strong> Miercuri, Sâmbătă, Duminică
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
